import { FormControl, FormGroup } from "@angular/forms";
import { OriginDataType } from "./flowsPrequalification.interface";
import { ProductState } from "./products.interfaces";
import type { FileConfig } from "src/ui/components/form-builder";

export interface SyncFlowFormFieldRequest {
  fieldId: string;
  originId: string;
  originalName: string;
  isMandatory: boolean;
  dataType: string;
  metadata?: {
    options?: string[];
    isExtended?: boolean;
    placeholder?: string;
    [key: string]: any;
  };
}

export interface SyncFlowFormSectionRequest {
  title: string;
  description?: string;
  fields: SyncFlowFormFieldRequest[];
  order: number;
}

export interface FlowFormFooterItem {
  type: 'text' | 'consent';
  content: string;
  link?: string;
  required?: boolean;
  order: number;
}

export interface SyncFlowFormRequest {
  formName: string;
  productName: string;
  description?: string;
  imageUrl: string | null;
  applicantType: 'individual' | 'legal_entity';
  sections: SyncFlowFormSectionRequest[];
  footer: FlowFormFooterItem[];
  isActive: boolean;
}

export interface FlowFormField {
  fieldId: string;
  originalName: string;
  label?: string;
  dataType: string;
  origin: string;
  originId: string;
  isMandatory: boolean;
  displayOrder?: number;
  metadata?: any;
  precalifFieldId?: string;
  fileConfig?: FileConfig;

  name?: string;
  type?: OriginDataType;
  required?: boolean;
  order?: number;
  originLabel?: string;
  dataTypeLabel?: string;
}

export interface FlowFormSection {
  title: string;
  description?: string;
  fields: FlowFormField[];
  order: number;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  statusCode: number;
  timestamp: string;
}

export interface ApiErrorResponse {
  code: string;
  message: string;
  statusCode: number;
  timestamp?: string;
  details?: {
    fieldsCount?: number;
    fields?: Array<{
      index: number;
      fieldId: string;
      label: string;
    }>;
    unsupportedFields?: Array<{
      name: string;
      type: string;
      fieldId: string;
    }>;
    supportedTypes?: string[];
    duplicatedFields?: string[];
  };
}

export interface FlowFormTenantCountry {
  isoCode?: string;
  dialingCode?: string;
}

export interface FlowFormHeader {
  name: string;
  description?: string;
  imageUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface FlowFormResponse {
  _id: string;
  formName: string;
  header?: FlowFormHeader;
  formCode: string;
  formUrl: string;
  country?: FlowFormTenantCountry;
  formStatus: 'draft' | 'published' | 'archived';
  productName: string;
  description?: string;
  imageUrl?: string | null;
  applicantType: 'individual' | 'legal_entity';
  sections: FlowFormSection[];
  footer?: FlowFormFooterItem[];
  productId: string;
  flowId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  version?: number;
  isCurrentVersion?: boolean;
  parentFormId?: string;
  publishedAt?: string;
  status?: string;
  showProgressBar?: boolean;
}

export interface FlowFormTemplateResponse {
  filename: string;
  contentType: string;
  data: Blob;
}

export interface FlowFormSummaryResponse {
  _id: string;
  formCode: string;
  formName: string;
  formUrl: string;
  formStatus: 'draft' | 'published' | 'archived';
  productId: number;
  productName: string;
  applicantType: 'individual' | 'legal_entity';
  version: number;
  isCurrentVersion: boolean;
  isActive: boolean;
  sectionsCount: number;
  fieldsCount: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedFlowFormsResponse {
  timestamp: string;
  data: {
    totalItems: number;
    items: FlowFormSummaryResponse[];
    currentPage: number;
    itemsPerPage: number;
    totalPages: number;
  };
  message: string;
  statusCode: number;
}

export interface FlowFormFieldBuilder {
  fieldId: string;
  homologatedName: string;
  originalName: string;
  origin: string;
  originLabel: string;
  dataType: string;
  dataTypeLabel: string;
  isMandatory: boolean;
  mandatoryLabel: string;
  metadata?: {
    options?: string[];
    isExtended?: boolean;
    placeholder?: string;
    [key: string]: any;
  };
}

export type TransitionType =
  | 'single_to_single'
  | 'no_change';

export interface TransitionContext {
  from: 'individual' | 'legal_entity';
  to: 'individual' | 'legal_entity';
  type: TransitionType;
  disabledFormType?: 'individual' | 'legal_entity';
  newFormType?: 'individual' | 'legal_entity';
}
export interface ProductsFlowsFullAPIResponse<T>
  extends ApiResponse<ProductsFlowsAPIResponse<T>> {
  message: string;
  statusCode: number;
  timestamp: string;
}
export interface ProductsFlowsAPIResponse<T> {
  totalItems: number;
  items: T[];
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
}

export interface FlowFormResponseSend {
  source: string;
  deviceType: string;
  sections: FlowFormSectionSend[];
  consents: FlowFormConsent[];
}

export interface PublicFormSubmitResponse {
  responseId: string;
  status: string;
  message: string;
  applicationNumber?: string;
  submittedAt: string;
}

export type PublicSectionNavigation = 'LINEAR' | 'CONDITIONAL' | 'ENGINE';

export interface PublicFormField {
  originalName: string;
  label?: string;
  dataType?: string;
  isMandatory?: boolean;
  displayOrder?: number;
  metadata?: Record<string, unknown>;
  fieldId?: string;
  originId?: string;
  fieldInstanceId?: string;
}

export interface PublicFormSection {
  title: string;
  description?: string;
  fields: PublicFormField[];
  order?: number;
  columns?: 1 | 2 | 3;
  collapsible?: boolean;
  visibility?: Record<string, unknown>;
  sectionKey: string;
  keyIsAmbiguous?: boolean;
  navigation: PublicSectionNavigation;
}

export interface PublicFormFooterItem {
  type: string;
  content?: string;
  link?: string;
  required?: boolean;
  order?: number;
  metadata?: Record<string, unknown>;
}

export interface PublicFormResponse {
  formId: string;
  formCode: string;
  formName: string;
  formUrl: string;
  productId: number | string;
  productName: string;
  description?: string;
  imageUrl?: string;
  applicantType: 'individual' | 'legal_entity';
  header?: FlowFormHeader;
  formMode?: 'INTERNAL' | 'CLIENT' | 'BOTH';
  showProgressBar?: boolean;
  navigationMode?: string;
  defaultColumns?: 1 | 2 | 3;
  sections: PublicFormSection[];
  footer: PublicFormFooterItem[];
  country?: FlowFormTenantCountry;
}

export interface PublicFormSectionPathResponse {
  sectionKeys: string[];
  nextSectionKey: string | null;
  completed: boolean;
}

export interface TableRowFilter {
  column: string;
  value: string | number | null;
}

export interface TableRowsQuery {
  filters?: TableRowFilter[];
  columns?: string[];
  limit?: number;
  offset?: number;
}

export interface UploadedFieldFile {
  url: string;
  name?: string;
}

export interface FlowFormConsent {
  content: string;
  accepted: boolean;
}

export interface FlowFormSectionSend {
  title: string;
  fields: FlowFormFieldSection[];
}

export interface FlowFormFieldSection {
  fieldName: string;
  label: string;
  value: unknown;
  dataType: string;
  isMandatory: boolean;
}


export interface Field {
    fieldId: string;
    name: string;
    originalName?: string;
    origin: string;
    originLabel: string;
    required: boolean;
    dataType: string;
    dataTypeLabel: string;
    mandatoryLabel: string;
    isExtended?: boolean;
    listElements?: string[];
    isCustomField?: boolean;
}

export interface Section {
    id: string;
    title: string;
    description: string;
    fields: Field[];
    expanded: boolean;
    titleControl: FormControl;
    descriptionControl: FormControl;
}

export interface FooterItem {
    id: string;
    type: 'text' | 'consent';
    content: string;
    link?: string;
    required?: boolean;
    order: number;
}

export interface FormState {
    configurationForm: FormGroup;
    sections: Section[];
    footerItems: FooterItem[];
    footerItemControls: Map<string, FormControl>;
    showFormFooter: boolean;
    availableFields: Field[];
    filteredFields: Field[];
}

export interface ValidationResult {
    isValid: boolean;
    message?: string;
    fields?: Field[];
}

export interface AutoSaveContext {
    productId: string;
    productState: ProductState;
    draftCreated: boolean;
    formStates: Map<'individual' | 'legal_entity', FormState>;
    applicantType: 'individual' | 'legal_entity';
}

export interface AutoSaveCallbacks {
    hasChanges: () => boolean;
    createDraft: () => Promise<string>;
    saveToExisting: () => Promise<void>;
    onSaveSuccess?: () => void;
    onSaveError?: (error: any) => void;
    setCanDeactivate?: (value: boolean) => void;
}

export const ORIGIN_LABELS: Record<string, string> = {
    'CUSTOM': 'Personalizado',
    'BASIC': 'Básico',
    'BUREAU': 'Buró',
    'FINANCIAL': 'Financiero',
    'EXTERNAL': 'Externo',
    'SCORING': 'Scoring',
    'SCORECARD': 'Scorecard',
    'AML': 'AML',
    'CATALOG': 'Catálogo',
};

export const DATA_TYPE_LABELS: Record<string, string> = {
    'DATE': 'Fecha',
    'NUMBER': 'Número',
    'LIST': 'Lista',
    'TEXT': 'Texto',
    'BOOLEAN': 'Booleano',
    'TEXTAREA': 'Área de texto'
};

export function mapFieldLabels(field: any): any {
    return {
        ...field,
        originLabel: ORIGIN_LABELS[field.origin || field.originId] || field.originLabel || field.origin,
        dataTypeLabel: DATA_TYPE_LABELS[field.dataType] || field.dataTypeLabel || field.dataType,
        mandatoryLabel: field.isMandatory || field.required ? 'Obligatorio' : 'Opcional'
    };
}


export interface SummaryCostResponse {
    productId: string;
    totalCost: number;
    bureaus:   CostDetailResponse[];
    amlLists:  CostDetailResponse[];
    totalResponseTime: number;
    bureausCount: number;
    includedBureausCount?: number;
    onDemandBureausCount?: number;
    includedAmlListsCount?: number;
    onDemandAmlListsCount?: number;
}

export interface CostDetailResponse {
    id:   string;
    name: string;
    type: string;
    cost: number;
    typeInformation: string;
    responseTime?: number;
    updateFrequency?: string;
}

export interface validateFormResponse {
    isComplete: boolean;
    errors:  validateFormError[];
}

export interface validateFormError {
    code: string;
    message: string;
    errors: validateFormErrorDetail[];
}

export interface validateFormErrorDetail{
  applicantType: 'individual' | 'legal_entity';
  field: string;
}